import { Link, useParams, useSearchParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { OrderPageBody } from "../customer-pages/OrderPageBody";
import type { OrderPageActions } from "../customer-pages/OrderPageBody";
import { useGoTo, useLinkRead } from "../customer-pages/useLinkRead";
import {
  CisRecordError,
  CisRecordLoading,
  CisRecordNotFound,
} from "./CisRecordStates";
import { orderArrival, payUrlFor, poNumberUrlFor } from "./cisOrderActions";
import { usePortalPageSlots } from "./PortalPageSlots";
import { usePortalViewer } from "./PortalViewerContext";
import { ArrowLeft } from "lucide-react";

// A CIS order's own page for the signed-in customer (customer-storefront P4g, spec 26 section 9B): the emailed
// link's order page body, unchanged, read by order number with the session (`GET /views/customer/order-page/
// {order_no}`) inside the portal's layout. It opens at `/orders/{order_no}`, the address the card checkout
// returns to, from the Orders list and from PortalOrderRoute, which tells it from a Tharstern job's.
//
// What the person may do is the read's: each action goes to a URL the read named (`pay_url`, `po_number_owed`,
// the artwork and proof URLs), null where it is not available here, and is called as the signed-in person. The
// page builds no URL and holds no rule: who may act, what is owed and every sentence are the server's. The staff
// view-as shell reads the same page from its mirror, which names no URL that acts, and hands the body no
// actions: staff look at the customer's page, they do not act as the customer.

export function PortalCisOrderPage() {
  const { orderId: orderNo = "" } = useParams<{ orderId: string }>();
  const adapter = useCustomerDataAdapter();
  const { viewKey, buildPortalUrl, isStaffView } = usePortalViewer();
  const [searchParams] = useSearchParams();
  const goTo = useGoTo();
  const slots = usePortalPageSlots();
  const arrival = orderArrival(searchParams);

  // A return from the card checkout reads the order again (the same route, so no remount).
  const { state, reload } = useLinkRead(
    `${viewKey}|${orderNo}|${arrival ?? ""}`,
    async () => {
      const page = await adapter.getOrderPage(orderNo);
      return page
        ? { kind: "ok" as const, view: page }
        : { kind: "not_found" as const };
    },
    "We couldn't load this order.",
  );

  const ordersTo = buildPortalUrl(paths.portalOrders());

  if (state.kind === "loading") return <CisRecordLoading what="order" />;
  if (state.kind === "error")
    return (
      <CisRecordError
        what="order"
        message={state.message}
        onRetry={() => void reload()}
      />
    );
  if (state.kind === "not_found")
    return <CisRecordNotFound what="order" backTo={ordersTo} />;

  const page = state.view;
  const actions: OrderPageActions | null = isStaffView
    ? null
    : {
        pay: async (addedLineRef) => {
          const { checkout_url } = await adapter.startOrderCardPayment(
            payUrlFor(page, addedLineRef),
            addedLineRef,
          );
          goTo(checkout_url);
        },
        givePoNumber: (number) =>
          adapter.giveOrderPoNumber(poNumberUrlFor(page), number),
        reload,
        writes: adapter.artwork,
        fetchFile: adapter.fetchFile,
      };

  return (
    <OrderPageBody
      backLink={
        <Link
          to={ordersTo}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Orders
        </Link>
      }
      page={page}
      arrival={arrival}
      loadKitDrops={adapter.getKitDrops}
      actions={actions}
      slot={slots.orderPage?.(page, reload)}
    />
  );
}
